import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { CheckCircle2, FileText, Loader2 } from "lucide-react";

import { axiosInstance } from "@/src_new/shared/lib/axiosInstance";
import { showToast } from "@/src_new/shared/lib/toast";
import { uploadOcrDocument } from "@/src_new/shared/services/ocr";
import { extractStructuredOcrData } from "@/src_new/shared/utils/documentOcr";
import { WrapperInput } from "./WrapperInput";
import { BooleanRecord } from "../types";
import {
  DocumentUploadField,
  DocumentUploadGridProps,
  FieldKey,
} from "../types/documentuploadtype";
import { App_Url_New } from "../common/static";

function appendFormFields(
  formData: FormData,
  fields?: Record<string, string | number | boolean | null | undefined>
) {
  Object.entries(fields || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });
}

function getDocumentLabel<TValues extends BooleanRecord>(field: DocumentUploadField<TValues>) {
  return field.label || field.title || "Document";
}

function getUploadSuccessMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} uploaded successfully.`;
}

function getUploadWithOcrWarningMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} uploaded successfully, but extraction failed.`;
}

function getUploadFailureMessage<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>
) {
  return `${getDocumentLabel(field)} upload failed. Please try again.`;
}

function getDocumentUploadToast<TValues extends BooleanRecord>(
  field: DocumentUploadField<TValues>,
  ocrStatus: "completed" | "failed" | "skipped" | "not_requested"
) {
  if (ocrStatus === "failed") {
    return {
      message: getUploadWithOcrWarningMessage(field),
      type: "warning" as const,
    };
  }

  return {
    message: getUploadSuccessMessage(field),
    type: "success" as const,
  };
}

export function DocumentUploadGrid<TValues extends BooleanRecord>({
  fields,
  values,
  onChange,
  label,
  description,
  error,
  fieldErrors: externalFieldErrors,
  uploadEndpoint,
  uploadFileFieldName = "file",
  uploadFields,
  ocrEndpointPath,
  ocrFileFieldName = "files",
  ocrFields,
  accept = App_Url_New.defaultAccept,
  maxFileSizeMb = 5,
  initialDocuments,
  onUploadComplete,
}: DocumentUploadGridProps<TValues>) {
  const [files, setFiles] = useState<Partial<Record<FieldKey<TValues>, File>>>({});
  const [previews, setPreviews] = useState<Partial<Record<FieldKey<TValues>, string>>>({});
  const [processing, setProcessing] = useState<Partial<Record<FieldKey<TValues>, boolean>>>({});
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey<TValues>, string>>>({});

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((preview) => {
        if (preview) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [previews]);

  const handleFileChange =
    (field: DocumentUploadField<TValues>) => async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file) {
        return;
      }

      if (file.size > maxFileSizeMb * 1024 * 1024) {
        showToast(`File size must be less than ${maxFileSizeMb} MB.`, "error");
        setFieldErrors((current) => ({
          ...current,
          [field?.key]: `File size must be less than ${maxFileSizeMb} MB`,
        }));
        return;
      }

      setProcessing((current) => ({ ...current, [field?.key]: true }));
      setFieldErrors((current) => ({ ...current, [field?.key]: "" }));
      let toastPayload:
        | {
            message: string;
            type: "success" | "error" | "warning";
          }
        | undefined;

      try {
        let uploadResponse: unknown;
        let ocrResponse: unknown;
        let ocrStatus: "completed" | "failed" | "skipped" | "not_requested" = ocrEndpointPath
          ? "failed"
          : "not_requested";

        if (uploadEndpoint) {
          const formData = new FormData();
          formData.append(uploadFileFieldName, file);
          appendFormFields(formData, uploadFields);
          appendFormFields(formData, field?.uploadFields);

          const response = await axiosInstance.post(uploadEndpoint, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          uploadResponse = response.data;
        }

        if (ocrEndpointPath && field?.enableOcr !== false) {
          try {
            ocrResponse = await uploadOcrDocument({
              endpointPath: ocrEndpointPath,
              fields: {
                ...ocrFields,
                ...field?.ocrFields,
              },
              files: [{ name: ocrFileFieldName, file }],
            });
            ocrStatus = "completed";
          } catch (ocrError) {
            console.error("Document OCR failed:", ocrError);
            ocrStatus = "failed";
            setFieldErrors((current) => ({
              ...current,
              [field?.key]:
                "Document uploaded, but analysis failed. You can continue or re-upload.",
            }));
          }
        } else if (field?.enableOcr === false) {
          ocrStatus = "skipped";
        }

        toastPayload = getDocumentUploadToast(field, ocrStatus);

        setFiles((current) => ({ ...current, [field?.key]: file }));
        setPreviews((current) => {
          const previousPreview = current[field?.key];
          if (previousPreview) {
            URL.revokeObjectURL(previousPreview);
          }

          return {
            ...current,
            [field?.key]: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
          };
        });
        onChange({
          ...values,
          [field?.key]: true,
        });
        try {
          onUploadComplete?.({
            field,
            file,
            uploadResponse,
            ocrResponse,
            persistedDocument: {
              fieldKey: field.key,
              filename: file.name,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
              uploadedAt: new Date().toISOString(),
              uploadResponse,
              ocrResponse,
              ocrEnabled: field?.enableOcr !== false,
              ocrRequested: Boolean(ocrEndpointPath) && field?.enableOcr !== false,
              ocrStatus,
              insuranceType:
                typeof field?.ocrFields?.insurance_type === "string"
                  ? field.ocrFields.insurance_type
                  : undefined,
              structuredData: extractStructuredOcrData(ocrResponse),
              upload: {
                fileName: file.name,
                response: uploadResponse,
              },
              ocr: {
                response: ocrResponse,
                status: ocrStatus,
                insuranceType:
                  typeof field?.ocrFields?.insurance_type === "string"
                    ? field.ocrFields.insurance_type
                    : undefined,
                structuredData: extractStructuredOcrData(ocrResponse),
              },
            },
          });
        } catch (uploadCompleteError) {
          console.error("Document upload completion handler failed:", uploadCompleteError);
        }
      } catch (uploadError) {
        console.error("Document upload failed:", uploadError);
        toastPayload = {
          message: getUploadFailureMessage(field),
          type: "error",
        };
        setFieldErrors((current) => ({
          ...current,
          [field?.key]: "Failed to upload document. Please try again.",
        }));
      } finally {
        if (toastPayload) {
          window.setTimeout(() => {
            showToast(toastPayload.message, toastPayload.type);
          }, 0);
        }
        setProcessing((current) => ({ ...current, [field?.key]: false }));
      }
    };

  const grid = (
    <div className="grid gap-4 md:grid-cols-2">
      {fields?.map((field) => {
        const uploadedFile = files[field?.key];
        const persistedDocument = initialDocuments?.[field?.key];
        const isUploaded = values[field?.key] === true;
        const isProcessing = processing[field?.key] === true;
        const previewUrl = previews[field?.key];
        const isPdf = uploadedFile?.type === "application/pdf";
        const persistedFileName = persistedDocument?.fileName || persistedDocument?.filename;
        const isPersistedPdf = persistedDocument?.mimeType === "application/pdf";
        const uploadedFileName = uploadedFile?.name || persistedFileName;
        const hasUploadedDocument = Boolean(uploadedFileName);
        const cardError = fieldErrors[field?.key] || externalFieldErrors?.[field?.key];

        return (
          <div key={String(field?.key)} className="space-y-2">
            <WrapperInput label={field?.label} error={cardError} required={field?.required}>
              <label
                className={`relative flex min-h-[140px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed p-5 text-center transition hover:border-[#07625C] hover:bg-gray-50  ${
                  isUploaded
                    ? "border-theme-primary bg-green-50/40"
                    : "border-theme-border-strong bg-white"
                } ${cardError ? "border-red-500 bg-red-50/40" : ""} ${
                  isProcessing ? "cursor-wait opacity-75" : ""
                }`}
              >
                {isProcessing ? (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-theme-surface/80">
                    <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-theme-primary">
                      Analyzing...
                    </p>
                  </div>
                ) : null}

                {previewUrl && !isPdf ? (
                  <>
                    <div
                      className="absolute inset-0 z-0"
                      style={{
                        backgroundImage: `url(${previewUrl})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                      }}
                    />
                    <div className="absolute inset-0 z-0 bg-theme-text/40" />
                  </>
                ) : null}

                <div className="relative z-10 flex w-full flex-col items-center">
                  {hasUploadedDocument ? (
                    <CheckCircle2
                      className={`mb-2 h-10 w-10 ${
                        previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                      }`}
                    />
                  ) : (
                    <span
                      className={`mb-2 text-3xl ${
                        previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                      }`}
                    >
                      {field?.icon || <FileText className="h-9 w-9" />}
                    </span>
                  )}

                  <p
                    className={`text-[12px] font-semibold ${
                      previewUrl ? "text-theme-text-inverse" : "text-theme-text-muted"
                    }`}
                  >
                    {field?.title}
                  </p>

                  <div className="mt-2  w-full px-2">
                    {!hasUploadedDocument ? (
                      <p className="text-center text-xs text-theme-text-soft">
                        {field?.helper || "Click to upload (PDF / JPG / PNG)"}
                      </p>
                    ) : (
                      <p
                        className={`max-w-full break-words text-center text-xs font-medium leading-5 ${
                          previewUrl ? "text-theme-text-inverse" : "text-theme-primary"
                        }`}
                      >
                        {uploadedFileName}
                      </p>
                    )}
                  </div>
                </div>

                <input
                  type="file"
                  className="hidden"
                  accept={accept}
                  onChange={handleFileChange(field)}
                  disabled={isProcessing}
                />
              </label>
            </WrapperInput>
          </div>
        );
      })}
    </div>
  );

  if (!label && !description && !error) {
    return grid;
  }

  return (
    <WrapperInput label={label || ""} description={description} error={error}>
      {grid}
    </WrapperInput>
  );
}
